import type { BoxScore, GameStatistics, ScheduleEventGameData, SingleRecordResponse } from '~/types'
import { scheduleEventQuarterModelDto } from '~/dto/scheduleEventQuarterModelDto'

interface Options {
  withBoxScore?: boolean
  withStatistics?: boolean
  withPlays?: boolean
}

const defaultOptions: Options = {
  withBoxScore: false,
  withStatistics: false,
  withPlays: false,
}

export const useLiveScheduleEvent = (srIdRef: MaybeRefOrGetter<string>, options: Options = defaultOptions) => {
  const logger = useLogger(`useLiveScheduleEvent:${unref(srIdRef)}`)

  const { getStreamById, processMessages, addBoxScore, addGameStatistics, addPlaysByPeriods } = useLiveGamesStore()
  const { messages: searchMessages } = useStreamApi()
  const { show: showBoxScore } = useBoxScoreApi()

  const sportRadarId = ref<string>()
  const loading = ref<boolean>(false)
  const stream = computed<Maybe<ScheduleEventGameData.Model>>(() => getStreamById(sportRadarId.value))

  const loadBoxScore = async (): Promise<undefined | SingleRecordResponse<BoxScore.FullModel>> => {
    if (!sportRadarId.value) return undefined
    try {
      const include: BoxScore.Include[] = []

      if (options.withBoxScore) {
        include.push('scheduleEventBoxScore')
      }

      if (options.withStatistics) {
        include.push('scheduleEventTeamStatistics')
        include.push('scheduleEventTeamStatistics.team')
      }

      if (options.withPlays) {
        include.push('scheduleEventQuarters')
      }

      return await showBoxScore(sportRadarId.value, include)
    }
    catch (ex: unknown) {
      logger.error(String(ex))

      return undefined
    }
  }

  const fetch = async (): Promise<Maybe<ScheduleEventGameData.Model>> => {
    if (!sportRadarId.value) return null

    loading.value = true

    try {
      const [
        messages,
        boxScore,
      ] = await Promise.all([
        searchMessages(sportRadarId.value),
        loadBoxScore(),
      ])

      processMessages(messages.data)

      if (boxScore && boxScore?.data?.scheduleEventBoxscore) {
        const boxScoreData = boxScore.data!.scheduleEventBoxscore!.vendorRawData
        addBoxScore(boxScoreData)
      }

      if (boxScore && boxScore?.data?.homeTeamStatistic?.statistic && boxScore?.data?.awayTeamStatistic?.statistic) {
        const gameStatistics: GameStatistics.Model = {
          id: sportRadarId.value,
          statistics: {
            home: boxScore.data.homeTeamStatistic.statistic,
            away: boxScore.data.awayTeamStatistic.statistic,
          },
        }

        addGameStatistics(gameStatistics)
      }

      if (boxScore && boxScore?.data?.scheduleEventQuarters) {
        const data = boxScore?.data?.scheduleEventQuarters?.map(scheduleEventQuarterModelDto) || []

        addPlaysByPeriods(sportRadarId, data)
      }

      return getStreamById(sportRadarId)
    }
    catch (ex: unknown) {
      logger.error(String(ex), ex)

      throw ex
    }
    finally {
      loading.value = false
    }
  }

  watchEffect(async () => {
    sportRadarId.value = toValue(srIdRef)
  })

  return {
    stream,

    fetch,
  }
}
